import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Smile, Meh, Frown, Star, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";

const BUCKET_STYLE = {
  promoter: { label: "Promotor", icon: Smile, color: "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" },
  passive: { label: "Neutro", icon: Meh, color: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
  detractor: { label: "Detrator", icon: Frown, color: "bg-red-500/10 text-red-700 border-red-500/30" },
};

function Tile({ label, value, sub, accent }) {
  return (
    <Card className="p-6 rounded-tenant border-border animate-fade-up">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-5">{label}</div>
      <div className={`font-display font-bold tracking-tight text-3xl ${accent ? "text-tenant" : ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </Card>
  );
}

export default function TenantNPS() {
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, l] = await Promise.all([
        api.get("/tenant/nps/stats"),
        api.get("/tenant/nps", { params: { status: "responded", limit: 50 } }),
      ]);
      setStats(s.data);
      setItems(l.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-10 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="p-6 md:p-10 max-w-7xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground flex items-center gap-2">
          <Star className="w-3.5 h-3.5" /> Pesquisa de Satisfação
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Pesquisa de Satisfação após o atendimento</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Assim que você marca um atendimento como <strong>Concluído</strong>, o sistema gera um link único de pesquisa vinculado a esse atendimento. Você pode enviar direto no WhatsApp do cliente com 1 clique.
        </p>
      </div>

      <Card className="p-5 mb-6 rounded-tenant border-tenant/30 bg-tenant/5" data-testid="nps-howto">
        <div className="flex items-start gap-3">
          <Star className="w-5 h-5 text-tenant shrink-0 mt-0.5" />
          <div className="flex-1 space-y-3">
            <div>
              <div className="font-semibold mb-1">Como funciona a pesquisa de satisfação — passo a passo</div>
              <p className="text-sm text-muted-foreground">
                Envio imediato após o atendimento. Sem esperar 24h. Sem depender de automação.
              </p>
            </div>

            <div className="grid md:grid-cols-4 gap-3 text-xs">
              <div className="bg-background border border-border rounded-lg p-3">
                <div className="font-bold text-tenant mb-1">1. Conclua o atendimento</div>
                <div className="text-muted-foreground">Em <strong>Agenda</strong>, clique no ✓ verde para marcar como &quot;Concluído&quot;.</div>
              </div>
              <div className="bg-background border border-border rounded-lg p-3">
                <div className="font-bold text-tenant mb-1">2. Link gerado na hora</div>
                <div className="text-muted-foreground">O sistema cria o link único da pesquisa vinculado a esse atendimento imediatamente.</div>
              </div>
              <div className="bg-background border border-border rounded-lg p-3">
                <div className="font-bold text-tenant mb-1">3. Envie no WhatsApp</div>
                <div className="text-muted-foreground">Aparece o botão <strong>Enviar pesquisa via WhatsApp</strong>. Um clique abre o chat do cliente com a mensagem pronta.</div>
              </div>
              <div className="bg-background border border-border rounded-lg p-3">
                <div className="font-bold text-tenant mb-1">4. Cliente responde</div>
                <div className="text-muted-foreground">Nota 0-10 + comentário. Tudo aparece nas métricas e na lista abaixo.</div>
              </div>
            </div>

            <div className="flex items-center gap-2 text-xs pt-1">
              <span className="text-muted-foreground">Cálculo: NPS = % Promotores (9-10) − % Detratores (0-6)</span>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
        <Tile label="Nota de Satisfação" value={stats.nps} sub={`${stats.responses} respostas em ${stats.window_days} dias`} accent />
        <Tile label="Promotores" value={stats.buckets.promoter} sub="notas 9 e 10" />
        <Tile label="Neutros" value={stats.buckets.passive} sub="notas 7 e 8" />
        <Tile label="Detratores" value={stats.buckets.detractor} sub="notas 0-6" />
      </div>

      <Card className="rounded-tenant border-border overflow-hidden">
        <div className="px-6 py-4 border-b border-border flex items-center gap-2">
          <MessageSquare className="w-5 h-5" />
          <div className="font-display text-xl font-semibold">Respostas recentes</div>
          <span className="ml-auto text-xs text-muted-foreground">{stats.pending} pendentes</span>
        </div>
        {items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            Ainda sem respostas. Assim que um atendimento for concluído, aparecerá aqui o botão para enviar a pesquisa direto no WhatsApp do cliente.
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((r) => {
              const b = BUCKET_STYLE[r.bucket] || BUCKET_STYLE.passive;
              const Icon = b.icon;
              return (
                <li key={r.id} className="px-6 py-4">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-lg grid place-items-center font-display font-bold text-xl ${b.color} border`}>
                      {r.score}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{r.client_name}</span>
                        <Badge variant="outline" className={b.color}>
                          <Icon className="w-3 h-3 mr-1" /> {b.label}
                        </Badge>
                      </div>
                      {r.comment && <div className="text-sm text-muted-foreground mt-1 italic">&ldquo;{r.comment}&rdquo;</div>}
                      <div className="text-xs text-muted-foreground mt-1">{new Date(r.responded_at).toLocaleString("pt-BR")}</div>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
